import React from "react"
import gql from "graphql-tag"
import { useQuery } from "@apollo/client/react"
import {
  HoverCard,
  HoverCardTrigger,
  HoverCardContent,
} from "@love-irish/ui-components"
import {
  LevelProgressBar,
  type LevelProgressData,
} from "@love-irish/adventure-level-progress"
import Content from "@love-irish/core-content"
import { useSession } from "@love-irish/application-session-provider"
import { ConnectedFollowButton } from "@love-irish/profile-follow-button"

export interface PopoverUser {
  /** The profile slug (no leading @). */
  username: string
  name?: string | null
  level?: number | null
  xp?: number | null
  shortBio?: string | null
  avatarUrl?: string | null
  /** The profile banner image (profiles are the public entity and carry a banner). */
  bannerUrl?: string | null
  /** A system-managed account (Tadhg, Grug, ...). */
  isNpc?: boolean
  /** A subscriber (not wired yet). */
  isSupporter?: boolean
  /** ISO date the profile was created, shown as "Joined ...". */
  joinedAt?: string | null
  /** Canonical level/XP progress — renders the bar beside the avatar. */
  levelProgress?: LevelProgressData | null
  /** GraphQL gid of the profile. When present, a follow button renders. */
  profileId?: string | null
  /** Whether the viewer already follows this profile. */
  isFollowing?: boolean
  /** True when the popover is for the viewer's own profile — hides the follow button. */
  canEdit?: boolean
}

const profilePath = (username: string) => `/@${username}`

/** Marks a system-managed (NPC) account. Native-title tooltip explains it. */
export const NpcBadge: React.FC<{ className?: string }> = ({ className }) => (
  <span
    title="System-managed account"
    data-testid="npc-badge"
    className={`inline-flex cursor-help items-center rounded border border-secondary-300 px-1 text-[10px] font-semibold uppercase text-muted-foreground ${
      className ?? ""
    }`}
  >
    NPC
  </span>
)

export const LevelBadge: React.FC<{ level: number; className?: string }> = ({
  level,
  className,
}) => (
  <span
    data-testid="level-badge"
    className={`inline-flex items-center rounded border border-secondary-300 px-1 text-[10px] font-semibold uppercase text-muted-foreground ${
      className ?? ""
    }`}
  >
    LVL {level}
  </span>
)

/** For subscribers (not wired yet; ProfileBadges renders it once profiles report isSupporter). */
export const SupporterBadge: React.FC<{ className?: string }> = ({
  className,
}) => (
  <span
    title="Supporter — subscribed to love.irish"
    data-testid="supporter-badge"
    className={`inline-flex items-center gap-0.5 rounded border border-earth-300 bg-earth-50 px-1 text-[10px] font-semibold uppercase text-earth-700 ${
      className ?? ""
    }`}
  >
    ♥ Supporter
  </span>
)

export interface ProfileBadgeInfo {
  level?: number | null
  isNpc?: boolean
  isSupporter?: boolean
}

// Central registry: one entry per badge, in display order. Add future badges (supporter, staff, ...)
// here and every profile surface picks them up.
const PROFILE_BADGES: {
  key: string
  show: (profile: ProfileBadgeInfo) => boolean
  render: (profile: ProfileBadgeInfo) => React.ReactNode
}[] = [
  { key: "npc", show: (p) => Boolean(p.isNpc), render: () => <NpcBadge /> },
  {
    key: "supporter",
    show: (p) => Boolean(p.isSupporter),
    render: () => <SupporterBadge />,
  },
  {
    key: "level",
    show: (p) => p.level != null,
    render: (p) => <LevelBadge level={p.level as number} />,
  },
]

/** Renders the set of badges a profile has earned (NPC, level, supporter, ...) from one registry. */
export const ProfileBadges: React.FC<{
  profile: ProfileBadgeInfo
  className?: string
}> = ({ profile, className }) => {
  const badges = PROFILE_BADGES.filter((badge) => badge.show(profile))
  if (badges.length === 0) return null

  return (
    <span
      className={`inline-flex items-center gap-1 ${className ?? ""}`}
      data-testid="profile-badges"
    >
      {badges.map((badge) => (
        <React.Fragment key={badge.key}>{badge.render(profile)}</React.Fragment>
      ))}
    </span>
  )
}

const formatJoined = (iso?: string | null) => {
  if (!iso) return null
  const date = new Date(iso)
  return Number.isNaN(date.getTime())
    ? null
    : date.toLocaleDateString(undefined, { month: "short", year: "numeric" })
}

/**
 * The Discord-style card body: banner, avatar, @username + level/XP, a short shortBio, and a "View
 * profile" link. Purely presentational — pass it whatever fields you have; each renders when present,
 * so it degrades gracefully while the async fields (avatar, banner, shortBio) are still loading.
 */
export const UserCard: React.FC<{ user: PopoverUser }> = ({ user }) => (
  <div className="w-64 overflow-hidden rounded-md" data-testid="user-card">
    {user.bannerUrl ? (
      <img
        src={user.bannerUrl}
        alt=""
        className="pixel-art aspect-banner min-h-12 object-cover"
      />
    ) : (
      <div className="h-12 bg-secondary-100" />
    )}
    <div className="px-3 pb-3">
      <div className="-mt-6 mb-1 relative z-20 flex items-end gap-3 justify-between">
        {user.avatarUrl ? (
          <img
            src={user.avatarUrl}
            alt=""
            className="pixel-art w-[42px] shrink-0 border-4 aspect-square border-background bg-background"
          />
        ) : (
          <div className="h-12 w-12 shrink-0 rounded-md border-2 border-background bg-secondary-200" />
        )}
        {/* Level progress bar sits to the right of the avatar; the level badge is hidden in the name row. */}
        {user.levelProgress && (
          <div className="min-w-0 pb-1 text-xs">
            <LevelProgressBar progress={user.levelProgress} />
          </div>
        )}
      </div>

      <span className="flex flex-wrap items-center gap-1">
        <a
          href={profilePath(user.username)}
          className="font-semibold text-definition hover:underline"
        >
          @{user.username}
        </a>
        <ProfileBadges
          profile={{
            isNpc: user.isNpc,
            isSupporter: user.isSupporter,
          }}
        />
      </span>

      {(user.xp != null || formatJoined(user.joinedAt)) && (
        <div className="text-xs text-muted-foreground mt-2">
          {[
            user.xp != null ? `${user.xp.toLocaleString()} XP` : null,
            formatJoined(user.joinedAt)
              ? `Joined ${formatJoined(user.joinedAt)}`
              : null,
          ]
            .filter(Boolean)
            .join(" · ")}
        </div>
      )}

      {user.shortBio && (
        <Content
          className="mt-2 line-clamp-4 text-xs text-muted-foreground"
          content={user.shortBio}
        />
      )}

      <div className="mt-3 flex items-center justify-between gap-2">
        <a
          href={profilePath(user.username)}
          className="text-xs font-medium text-primary-600 hover:underline"
        >
          View profile &rarr;
        </a>
        {user.profileId && (
          <ConnectedFollowButton
            profileId={user.profileId}
            isFollowing={user.isFollowing}
            canEdit={user.canEdit}
            size="xs"
          />
        )}
      </div>
    </div>
  </div>
)

export const USER_POPOVER_PROFILE_QUERY = gql`
  query UserPopoverProfile($slug: String!) {
    profile(slug: $slug) {
      id
      type
      slug
      name
      currentLevel
      totalExperience
      levelProgress {
        currentLevel
        totalXp
        xpIntoLevel
        xpForLevel
        xpToNextLevel
        progressPercentage
      }
      shortBio
      createdAt
      isFollowing
      avatar {
        imageUrl
      }
      banner {
        imageUrl
      }
    }
  }
`

/**
 * Fetches the profile by slug (Apollo-cached, so repeated mentions cost one request) and renders the
 * card. Shows the known @username + level immediately, then streams in avatar/banner/shortBio when loaded,
 * so nothing blocks. Rendered inside the hover card, so the query only fires on hover.
 */
export const ConnectedUserCard: React.FC<{
  slug: string
  level?: number | null
}> = ({ slug, level }) => {
  const { data } = useQuery(USER_POPOVER_PROFILE_QUERY, { variables: { slug } })
  const profile = (data as { profile?: Record<string, any> } | undefined)
    ?.profile
  const { currentProfile } = useSession()

  return (
    <UserCard
      user={{
        ...profile,
        username: profile?.slug ?? slug,
        level: profile?.currentLevel ?? level,
        xp: profile?.totalExperience,
        avatarUrl: profile?.avatar?.imageUrl,
        bannerUrl: profile?.banner?.imageUrl,
        isNpc: profile?.type === "Profile::Npc",
        joinedAt: profile?.createdAt,
        levelProgress: profile?.levelProgress,
        profileId: profile?.id,
        isFollowing: profile?.isFollowing,
        // Hide the follow button on your own hover card.
        canEdit: Boolean(profile?.id && currentProfile?.id === profile.id),
      }}
    />
  )
}

/**
 * Wraps an @username mention: hovering opens the profile card (above the link) and lazily fetches the
 * profile by slug; clicking navigates to the profile. Only the username (and optionally a known level)
 * is needed — the card fetches the rest, so no media is loaded until someone actually hovers.
 *
 * ```tsx
 * <UserPopover username="myk" level={4}>@myk</UserPopover>
 * ```
 */
export const UserPopover: React.FC<{
  username: string
  level?: number | null
  children: React.ReactNode
}> = ({ username, level, children }) => (
  <HoverCard openDelay={150} closeDelay={100}>
    <HoverCardTrigger asChild>
      <a
        href={profilePath(username)}
        className="hover:underline inline-flex flex-nowrap gap-1 items-center"
      >
        {children}
      </a>
    </HoverCardTrigger>
    <HoverCardContent side="top" align="start" className="w-64 p-0">
      <ConnectedUserCard slug={username} level={level} />
    </HoverCardContent>
  </HoverCard>
)

export default UserPopover
